import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { documentWorkflowControllerBulkApplyDateStatusOptions } from '@dealstack/shared/lib/backendClient/@tanstack/react-query.gen';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { BULK_APPLY_DATE_TERMINAL_STATUSES } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/BulkApplyDateModal.types';
import type { UseBulkApplyDateStatusArgs } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useBulkApplyDateStatus.types';

const POLL_INTERVAL_MS = 2000;

export const useBulkApplyDateStatus = ({
	dealId,
	programmeId,
	jobId,
	enabled = true,
	onActive,
	onTerminal,
}: UseBulkApplyDateStatusArgs) => {
	const queryClient = useQueryClient();
	const documentListKey = formatUnicorn(
		API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_LIST_GET,
		{ dealId, programmeId }
	);

	const q = useQuery({
		...documentWorkflowControllerBulkApplyDateStatusOptions({
			path: { dealId, programmeId, jobId },
		}),
		enabled,
		// A probe, not a fetch: the job id is derived from the programme, so asking about a job
		// that does not exist is the normal case. Without this the cache-level handler toasts the
		// server's "No bulk post-dating job found" at the admin every time they open the modal.
		meta: { suppressErrorToast: true },
		refetchInterval: (query) => {
			const data = query.state.data;
			// A live job always answers its first poll and its data survives a later blip, so
			// stopping here only kills a loop with nothing to follow — the open-time probe's 404.
			if (!data) {
				return false;
			}
			if (BULK_APPLY_DATE_TERMINAL_STATUSES.includes(data.status)) {
				return false;
			}
			return POLL_INTERVAL_MS;
		},
	});

	useEffect(() => {
		if (!q.isSuccess) {
			return;
		}
		const data = q.data;
		if (!BULK_APPLY_DATE_TERMINAL_STATUSES.includes(data.status)) {
			onActive?.();
			return;
		}
		void (async () => {
			await queryClient.invalidateQueries({ queryKey: [documentListKey] });
			onTerminal?.(data);
		})();
	}, [q.isSuccess, q.data]);

	return q;
};
